/**
 * Proctoring Detector Math Library
 * Computes facial orientation (yaw, pitch) and eye gaze tracking (horizontal, vertical)
 * from MediaPipe FaceLandmarker output landmarks.
 */

/**
 * Calculates Euclidean distance between two 3D landmarks
 */
export const computeDistance = (p1, p2) => {
  return Math.sqrt(
    Math.pow(p1.x - p2.x, 2) +
    Math.pow(p1.y - p2.y, 2) +
    Math.pow(p1.z - p2.z, 2)
  );
};

/**
 * Computes head turn yaw (left/right) ratio
 * Cheeks: left (234), right (454)
 * Nose tip: 1
 * Returns value around 0.5 when looking straight
 */
export const getYawRatio = (landmarks) => {
  if (!landmarks || landmarks.length < 455) return 0.5;
  const pNose = landmarks[1];
  const pLeft = landmarks[234];
  const pRight = landmarks[454];
  
  const width = Math.abs(pRight.x - pLeft.x);
  if (width === 0) return 0.5;
  
  return (pNose.x - pLeft.x) / (pRight.x - pLeft.x);
};

/**
 * Computes head turn pitch (up/down) ratio
 * Forehead: 10
 * Chin: 152
 * Nose tip: 1
 * Returns value around 0.5 when looking straight
 */
export const getPitchRatio = (landmarks) => {
  if (!landmarks || landmarks.length < 153) return 0.5;
  const pNose = landmarks[1];
  const pForehead = landmarks[10];
  const pChin = landmarks[152];
  
  const height = Math.abs(pChin.y - pForehead.y);
  if (height === 0) return 0.5;
  
  return (pNose.y - pForehead.y) / (pChin.y - pForehead.y);
};

/**
 * Computes eye gaze tracking ratios for both eyes.
 * Left Eye Corners: Outer (263), Inner (362), Iris Center (473)
 * Right Eye Corners: Outer (33), Inner (133), Iris Center (468)
 * Left Eye Eyelids: Top (386), Bottom (374)
 * Right Eye Eyelids: Top (159), Bottom (145)
 */
export const getGazeRatios = (landmarks) => {
  if (!landmarks || landmarks.length < 474) {
    return { gazeL: 0.5, gazeR: 0.5, vGazeL: 0.5, vGazeR: 0.5 };
  }

  // Left Eye horizontal
  const leftOuter = landmarks[263];
  const leftInner = landmarks[362];
  const leftIris = landmarks[473];
  const rangeL = leftOuter.x - leftInner.x;
  const gazeL = rangeL !== 0 ? (leftIris.x - leftInner.x) / rangeL : 0.5;

  // Right Eye horizontal
  const rightOuter = landmarks[33];
  const rightInner = landmarks[133];
  const rightIris = landmarks[468];
  const rangeR = rightInner.x - rightOuter.x;
  const gazeR = rangeR !== 0 ? (rightIris.x - rightOuter.x) / rangeR : 0.5;

  // Left Eye vertical
  const leftTop = landmarks[386];
  const leftBottom = landmarks[374];
  const vRangeL = leftBottom.y - leftTop.y;
  const vGazeL = vRangeL !== 0 ? (leftIris.y - leftTop.y) / vRangeL : 0.5;

  // Right Eye vertical
  const rightTop = landmarks[159];
  const rightBottom = landmarks[145];
  const vRangeR = rightBottom.y - rightTop.y;
  const vGazeR = vRangeR !== 0 ? (rightIris.y - rightTop.y) / vRangeR : 0.5;

  return { gazeL, gazeR, vGazeL, vGazeR };
};

/**
 * Checks if face is within camera frame borders
 * Returns false if landmarks are too close to camera edge
 */
export const isFaceInBounds = (landmarks, margin = 0.02) => {
  if (!landmarks || landmarks.length === 0) return false;
  return landmarks.every(p => 
    p.x >= margin && p.x <= (1 - margin) &&
    p.y >= margin && p.y <= (1 - margin)
  );
};
